<template>
    <v-dialog v-model="dialogVisible" max-width="900px">
        <v-card>
            <v-card-title>
                <span class="headline">
                    {{
                        isEdited
                            ? $t("faq_web.edit_faq")
                            : $t("faq_web.add_faq")
                    }}
                </span>
            </v-card-title>

            <v-card-text>
                <v-container>
                    <v-form ref="form" v-model="valid" lazy-validation>
                        <v-row>
                            <!-- LANGUAGE TABS -->
                            <v-col cols="12" class="mb-4">
                                <v-btn-toggle
                                    v-model="activeLang"
                                    mandatory
                                    dense
                                >
                                    <!-- BASE TAB -->
                                    <v-btn
                                        value="base"
                                        small
                                        :class="
                                            activeLang === 'base'
                                                ? 'primary white--text'
                                                : ''
                                        "
                                    >
                                        {{ $t("faq_web.general") || "Obecné" }}
                                    </v-btn>

                                    <!-- CZ TAB -->
                                    <v-btn
                                        value="cz"
                                        small
                                        :class="
                                            activeLang === 'cz'
                                                ? 'primary white--text'
                                                : ''
                                        "
                                    >
                                        CZ
                                    </v-btn>

                                    <!-- OTHER LANGS -->
                                    <v-btn
                                        v-for="lang in filteredLanguages"
                                        :key="lang.code"
                                        :value="lang.code"
                                        small
                                        :class="
                                            activeLang === lang.code
                                                ? 'primary white--text'
                                                : ''
                                        "
                                    >
                                        {{ lang.name }} ({{ lang.code }})
                                    </v-btn>
                                </v-btn-toggle>
                            </v-col>

                            <!-- BASE TAB CONTENT -->
                            <template v-if="activeLang === 'base'">
                                <v-col cols="12">
                                    <v-switch
                                        v-model="formData.is_active"
                                        :label="$t('general.active')"
                                        color="green"
                                    ></v-switch>
                                </v-col>
                            </template>

                            <!-- CZ TAB CONTENT -->
                            <template v-else-if="activeLang === 'cz'">
                                <v-col cols="12" class="py-0">
                                    <v-text-field
                                        v-model="formData.question"
                                        :label="$t('faq_web.question_cz')"
                                        :rules="questionRules"
                                        required
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" class="py-0">
                                    <v-textarea
                                        v-model="formData.answer"
                                        :label="$t('faq_web.answer_cz')"
                                        :rules="answerRules"
                                        required
                                        :rows="3"
                                        auto-grow
                                    ></v-textarea>
                                </v-col>
                            </template>

                            <!-- OTHER LANGS CONTENT -->
                            <template v-else>
                                <div
                                    v-for="lang in filteredLanguages"
                                    v-if="activeLang === lang.code"
                                    :key="`lang-${lang.code}`"
                                    cols="12"
                                    style="width: 100%"
                                >
                                    <v-col cols="12" class="py-0">
                                        <v-text-field
                                            v-model="
                                                formData[
                                                    `question_${lang.code}`
                                                ]
                                            "
                                            :label="
                                                $t('faq_web.question_lang') +
                                                ' ' +
                                                lang.name +
                                                ' (' +
                                                lang.code +
                                                ')'
                                            "
                                            :rows="1"
                                        ></v-text-field
                                    ></v-col>
                                    <v-col cols="12" class="py-0">
                                        <v-textarea
                                            class="mt-2"
                                            v-model="
                                                formData[`answer_${lang.code}`]
                                            "
                                            :label="
                                                $t('faq_web.answer_lang') +
                                                ' ' +
                                                lang.name +
                                                ' (' +
                                                lang.code +
                                                ')'
                                            "
                                            :rows="2"
                                        ></v-textarea
                                    ></v-col>
                                </div>
                            </template>
                        </v-row>
                    </v-form>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="closeDialog">
                    {{ $t("general.cancel") }}
                </v-btn>
                <v-btn
                    color="blue darken-1"
                    text
                    @click="saveFaq"
                    :disabled="!valid"
                >
                    {{ $t("general.save") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: "FaqWebForm",

    props: {
        faq: {
            type: Object,
            required: true,
        },
        languages: {
            type: Array,
            required: true,
        },
        visible: {
            type: Boolean,
            default: false,
        },
        isEdited: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            valid: true,
            // default je BASE záložka (obecné)
            activeLang: "base",
            formData: {
                id: null,
                question: "",
                answer: "",
                is_active: 0,
            },
            questionRules: [(v) => !!v || this.$t("faq_web.question_required")],
            answerRules: [(v) => !!v || this.$t("faq_web.answer_required")],
        };
    },

    computed: {
        filteredLanguages() {
            // stejně jako u licence – filtrujeme jen CZ ven, base je virtuální
            return this.languages.filter((l) => l.code !== "cz");
        },
        dialogVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                if (!value) {
                    this.$emit("close");
                }
            },
        },
    },

    watch: {
        visible(val) {
            if (val) {
                // při otevření vždy začneme na BASE
                this.activeLang = "base";

                if (this.isEdited) {
                    this.formData = JSON.parse(JSON.stringify(this.faq));
                    this.ensureDynamicFields();
                } else {
                    this.resetForm();
                }
            }
        },
        languages() {
            this.ensureDynamicFields();
        },
    },

    methods: {
        ensureDynamicFields() {
            const data = { ...this.formData };

            this.filteredLanguages.forEach((lang) => {
                const qField = `question_${lang.code}`;
                const aField = `answer_${lang.code}`;

                if (data[qField] === undefined) data[qField] = "";
                if (data[aField] === undefined) data[aField] = "";
            });

            if (data.is_active === undefined || data.is_active === null) {
                data.is_active = 0;
            }

            this.formData = data;
        },

        resetForm() {
            const base = {
                id: null,
                question: "",
                answer: "",
                is_active: 0,
            };

            this.languages.forEach((lang) => {
                if (lang.code === "cz") return;
                base[`question_${lang.code}`] = "";
                base[`answer_${lang.code}`] = "";
            });

            this.formData = base;

            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        closeDialog() {
            this.$emit("close");
            this.resetForm();
        },

        async saveFaq() {
            if (this.$refs.form.validate()) {
                try {
                    if (this.isEdited) {
                        await this.updateFaq();
                    } else {
                        await this.createFaq();
                    }
                    this.closeDialog();
                } catch (error) {
                    console.error(error.response?.data || error);
                    this.$emit("snackMessage", "general.error", "error");
                }
            }
        },

        async createFaq() {
            const response = await axios.post("/faq-web", this.formData, {
                headers: {
                    Authorization:
                        "Bearer " +
                        this.$store.state.tokenData.user.access_token,
                },
            });

            const faq = response.data.faq || response.data;
            this.$emit("addFaq", faq);
            this.$emit(
                "snackMessage",
                "faq_web.created_successfully",
                "success"
            );
        },

        async updateFaq() {
            const response = await axios.put(
                `/faq-web/${this.faq.id}`,
                this.formData,
                {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                    },
                }
            );

            const faq = response.data.faq || response.data;
            this.$emit("updateFaq", faq);
            this.$emit(
                "snackMessage",
                "faq_web.updated_successfully",
                "success"
            );
        },
    },
};
</script>
